import React, { useCallback, useEffect, useRef, useState } from "react";
import JobCodeList from "../common/JobCodeList";
import CreditCustomerList from "../common/CreditCustomerList";
import Modal from "../common/CommonModal";
import { Search, UserPlus } from "lucide-react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import useApi from "../../hooks/useApi";

const PurchaseReturnGeneral = ({
  purchaseOrderData,
  setPurchaseOrderData,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT_NO,
  setSupplierData,
  selectedSupplier,
  setSelectedSupplier,
  supplierData,
}) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "", // 'add', 'cr-list'
    customer: null,
  });

  const autocompleteRef = useRef();
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const { request, loading } = useApi();

  const handleSupplierChange = (event, newValue) => {
    console.log("Supplier data new Value", newValue);
    setSelectedSupplier(newValue);
    setSupplierData(newValue);

    console.log("Selected Supplier", newValue);

    setPurchaseOrderData({
      ...purchaseOrderData,
      supplier: newValue,
      creditAcc: newValue?.ACCOUNT || "",
      trn: newValue?.TRN || "",
    });
  };
  const fetchCreditSuppliers = async (query = "-") => {
    const safeQuery = encodeURIComponent(query);
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT_NO}/all/-/${safeQuery}`;
    try {
      const response = await request({
        url: url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch credit suppliers", err);
      return [];
    }
  };

  const fetchSuggestions = useCallback(
    async (query) => {
      if (!query || query.trim().length === 0) {
        setSuggestions([]);
        return;
      }

      const freetext = query.trim();

      const result = await fetchCreditSuppliers(freetext);
      setSuggestions(result || []);
    },
    [BASE_URL, COMPANY_CODE, DEPARTMENT_NO]
  );

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [inputValue, fetchSuggestions]);

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: "",
      customer: null,
    });
  };
  const getOptionLabel = (option) => {
    if (!option) return "";
    return option.DESCRIPTION || option.name || "";
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
      <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <FormControl
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            <RadioGroup
              row
              name="purchaseType"
              value={purchaseOrderData?.purchaseType || "Local"}
              onChange={(e) =>
                setPurchaseOrderData({
                  ...purchaseOrderData,
                  purchaseType: e.target.value,
                })
              }
            >
              <FormControlLabel
                value="Local"
                control={<Radio size="small" />}
                label="Local"
              />
              <FormControlLabel
                value="Import"
                control={<Radio size="small" />}
                label="Import"
              />
            </RadioGroup>
          </FormControl>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-gray-600">PR No:</span>
            <span className="text-red-500 font-semibold">
              {purchaseOrderData?.documentNo}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TextField
            select
            label="VAT Status"
            variant="outlined"
            size="small"
            value={purchaseOrderData.vatStatus || "VAT Applied"}
            onChange={(e) =>
              setPurchaseOrderData({
                ...purchaseOrderData,
                vatStatus: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <MenuItem value="VAT Applied">VAT Applied</MenuItem>
            <MenuItem value="0% VAT">0% VAT</MenuItem>
            <MenuItem value="VAT Exempted">VAT Exempted</MenuItem>
            <MenuItem value="Out of Scope">Out of Scope</MenuItem>
          </TextField>

          <TextField
            label="PO Date"
            type="date"
            variant="outlined"
            size="small"
            value={
              purchaseOrderData.purchaseDate ||
              new Date().toISOString().split("T")[0]
            }
            onChange={(e) =>
              setPurchaseOrderData({
                ...purchaseOrderData,
                purchaseDate: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </div>
      </div>

      <div className="lg:col-span-4 col-span-1 flex flex-col gap-4">
        <Autocomplete
          ref={autocompleteRef}
          value={selectedSupplier}
          onChange={handleSupplierChange}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          options={suggestions}
          getOptionLabel={getOptionLabel}
          loading={loading}
          filterOptions={(x) => x} // Disable built-in filtering
          renderInput={(params) => (
            <TextField
              {...params}
              label="Supplier"
              variant="outlined"
              size="small"
              placeholder="Search supplier..."
              onKeyDown={(e) => {
                if (e.key === "F2") {
                  e.preventDefault();

                  // CLOSE AUTOCOMPLETE SUGGESTIONS
                  if (autocompleteRef.current) {
                    autocompleteRef.current.blur();
                  }
                  setModalState({
                    isOpen: true,
                    type: "cr-list",
                    customer: null,
                  });
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div className="flex items-center justify-between w-full">
                <span>{option.DESCRIPTION || option.name}</span>
                {option.ACCOUNT && (
                  <Chip
                    label={option.ACCOUNT}
                    size="small"
                    color={"primary"}
                    sx={{ height: 20, fontSize: 10 }}
                  />
                )}
              </div>
            </li>
          )}
          noOptionsText={
            inputValue.length === 0
              ? "Start typing to search..."
              : "No suppliers found"
          }
        />
        <div className="p-3 bg-gray-100 flex justify-between rounded-lg">
          <div>
            <span className="text-sm text-gray-600">Credit A/C:</span>{" "}
            <span className="font-bold text-orange-600">
              {supplierData?.ACCOUNT || "######"}
            </span>
          </div>
          <div>
            <span className="text-sm text-gray-600">Balance: </span>
            <span
              className={`font-bold ${supplierData?.BALANCE > 0
                  ? "text-green-600"
                  : supplierData?.BALANCE < 0
                    ? "text-red-500"
                    : "text-gray-600"
                }`}
            >
              {supplierData?.BALANCE ?? "0"}
            </span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 col-span-1 flex flex-col gap-4 justify-between">
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <TextField
              label="Order Ref"
              fullWidth
              size="small"
              variant="outlined"
              value={purchaseOrderData.invNumber || ""}
              onChange={(e) =>
                setPurchaseOrderData({
                  ...purchaseOrderData,
                  invNumber: e.target.value,
                })
              }
            />
            <TextField
              label="TRN"
              fullWidth
              size="small"
              variant="outlined"
              value={purchaseOrderData.trn || ""}
              required
              inputProps={{ maxLength: 15 }}
              error={
                purchaseOrderData.trn && purchaseOrderData.trn.length !== 15
              }
              helperText={
                purchaseOrderData.trn && purchaseOrderData.trn.length !== 15
                  ? "TRN must be exactly 15 digits"
                  : ""
              }
              onChange={(e) =>
                setPurchaseOrderData({
                  ...purchaseOrderData,
                  trn: e.target.value,
                })
              }
            />
          </div>
          <div className="flex gap-4">
            <TextField
              label="Attention"
              fullWidth
              size="small"
              variant="outlined"
              value={purchaseOrderData.unregSupplier || ""}
              onChange={(e) =>
                setPurchaseOrderData({
                  ...purchaseOrderData,
                  unregSupplier: e.target.value,
                })
              }
            />
            <TextField
              label="Job Code"
              fullWidth
              size="small"
              variant="outlined"
              value={purchaseOrderData.jobCode || ""}
              onChange={(e) =>
                setPurchaseOrderData({
                  ...purchaseOrderData,
                  jobCode: e.target.value,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "F2") {
                  e.preventDefault();
                  setModalState({
                    isOpen: true,
                    type: "job-list",
                  });
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setModalState({
                          isOpen: true,
                          type: "job-list",
                        })
                      }
                      size="small"
                      sx={{
                        padding: 0.3,
                      }}
                    >
                      <Search className=" text-gray-500" size={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={
          modalState.type === "add"
            ? "Add New Supplier"
            : modalState.type === "cr-list"
              ? "Select Supplier"
              : modalState.type === "job-list"
                ? "Select Job Code"
                : "Supplier"
        }
        size={modalState.type === "add" ? "xxl" : "xxl"}
        header={modalState.type === "add" ? false : true}
      >
        {modalState.type === "cr-list" && (
          <CreditCustomerList
            defaultType="Suppliers"
            handleCustomerSelect={(newValue) => {
              console.log("Supplier data new Value", newValue);
              setSelectedSupplier(newValue);
              setSupplierData(newValue);
              console.log("Selected Supplier", newValue);
              setPurchaseOrderData({
                ...purchaseOrderData,
                supplier: newValue,
                creditAcc:
                  purchaseOrderData.paymentMode === "cash"
                    ? ""
                    : newValue?.ACCOUNT || "",
                trn: newValue?.TRN || "",
              });
              closeModal();
            }}
          />
        )}{" "}
        {/* {modalState.type === "cash-supplier-list" && (
          <CashCustomerList
            type="supplier"
            handleCustomerSelect={(selectedItem) => {
              handleSupplierChange(null, selectedItem);
              setModalState({ isOpen: false, type: "", customer: null });
            }}
          />
        )} */}
        {modalState.type === "job-list" && (
          <JobCodeList
            open={modalState.isOpen}
            onClose={closeModal}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            onSelect={(job) => {
              setPurchaseOrderData({
                ...purchaseOrderData,
                jobCode: job.JobCode,
              });
              closeModal();
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default PurchaseReturnGeneral;
